import { observer } from 'mobx-react';
import type { FC, MouseEventHandler } from 'react';
import { useState } from 'react';
import icons from '@icons';
import { Picture } from '@ui';
import {
  mainPA,
  mainPAactive,
  ordersPA,
  ordersPAactive,
  productsPA,
  productsPAactive,
  rubleBonus,
  rubleBonusActive,
  recipesPA,
  recipesPAactive,
  profilePA,
  profilePAactive,
  settingsPA,
  settingsPAactive,
  exitPA,
  exitPAactive
} from '@images';
import { Link, useHistory, useLocation } from '@compat/router';
import LoadingSkeleton from '@components/LoadingSkeleton';
import HeaderAvatar from '../HeaderAvatar';
import useWindowSize from '@customHooks/useWindowSize';
import { preparePriceFloatObject } from '@utils/floatNumbers';
import { useStores } from '@root/src/stores';
import {
  personalAreaMenuItems,
  personalAreaExitTitle,
  personalAreaFeedbackTitle
} from '@routes/PersonalArea/personalAreaMenuItems';
import { REVIEWS_LEAVE_FEEDBACK_LINK } from '@routes/Reviews/constants';
import type { IconType } from '@root/src/types';
import './index.scss';

const {
  person: { black: personBlackFilled, red: personRedFilled }
} = icons.Fill;

const {
  fileText: { black: fileTextBlackOutlined, red: fileTextRedOutlined },
  messageCircle: { red: messageCircleRed, black: messageCircleBlack },
  bell: { black: bellBlackOutlined, red: bellRedOutlined }
} = icons.Outline;

const menuIconsByPath: Record<string, { icon: IconType; iconActive: IconType }> =
  {
    '/cabinet': { icon: mainPA, iconActive: mainPAactive },
    '/cabinet/orders': { icon: ordersPA, iconActive: ordersPAactive },
    '/cabinet/products': { icon: productsPA, iconActive: productsPAactive },
    '/cabinet/bonuses': { icon: rubleBonus, iconActive: rubleBonusActive },
    '/cabinet/notifications': {
      icon: bellBlackOutlined,
      iconActive: bellRedOutlined
    },
    '/cabinet/reviews': {
      icon: messageCircleBlack,
      iconActive: messageCircleRed
    },
    '/cabinet/cart-templates': {
      icon: fileTextBlackOutlined,
      iconActive: fileTextRedOutlined
    },
    '/cabinet/recipes': { icon: recipesPA, iconActive: recipesPAactive },
    '/cabinet/profile': { icon: profilePA, iconActive: profilePAactive },
    '/cabinet/settings': { icon: settingsPA, iconActive: settingsPAactive }
  };

const listOfRoutes = [
  ...personalAreaMenuItems
    .filter(item => !item.hideInHeaderDropdown)
    .map(item => ({
      title: item.title,
      path: item.path,
      exact: item.exact ?? false,
      ...menuIconsByPath[item.path]
    })),
  {
    isExit: true,
    title: personalAreaExitTitle,
    path: null,
    icon: exitPA,
    iconActive: exitPAactive
  }
];

// создаем массив вида [ {index: listOfRoutes.icon}, ...]
const defaultState = listOfRoutes.reduce(
  (acc, item, index) =>
    Object.assign(acc, { ...acc, [index]: { icon: item.icon } }),
  {}
);

const AccountBtnDesktop: FC = observer(() => {
  const { userStore, modalsStore, cartStore } = useStores();
  const { isTablet } = useWindowSize();
  const { changeAuthModalState } = modalsStore;
  const { isUserLoggedIn, isUserLoading, user, logout } = userStore;
  const [isShowMenu, setIsShowMenu] = useState(false);
  const [curIcons, setCurIcons] = useState(defaultState);
  const history = useHistory();
  // ЭТАП 1 · Шаг 3 — гидрация: bare `location` в рендере читал разные
  // источники на сервере (шим) и клиенте (window.location); useLocation — один.
  const location = useLocation();
  const [loginBtnIcon, setLoginBtnIcon] = useState(personBlackFilled);

  const onMouseEnter: MouseEventHandler = e => {
    const id = Number((e.target as HTMLDivElement)?.getAttribute('data-id'));
    setCurIcons(prev => ({
      ...prev,
      [id]: { icon: listOfRoutes[id].iconActive }
    }));
  };

  const onMouseLeave: MouseEventHandler = e => {
    const id = Number((e.target as HTMLDivElement).getAttribute('data-id'));
    setCurIcons(prev => ({
      ...prev,
      [id]: { icon: listOfRoutes[id].icon }
    }));
  };

  const onBtnClick: MouseEventHandler = e => {
    e.stopPropagation();
    if (!isTablet) {
      history.push('/cabinet');
    }
  };

  if (isUserLoading) {
    return (
      <LoadingSkeleton
        style={{
          width: 112,
          minWidth: 112,
          height: 40,
          borderRadius: 8
        }}
      />
    );
  } else {
    if (isUserLoggedIn) {
      const price = preparePriceFloatObject(
        Number(user?.bill?.billAmount) - Number(cartStore?.billPayed)
      );
      return (
        <div className='account-btn__wrapper'>
          <div
            onClick={onBtnClick}
            onMouseEnter={() => {
              setIsShowMenu(true);
            }}
            onMouseLeave={() => {
              setIsShowMenu(false);
            }}
            className='account-btn'
          >
            <HeaderAvatar />
            <div className='account-btn__bonuses-wrapper'>
              <div className='account-btn__bonuses'>
                {price?.wholePart || 0}
                {price?.floatPart && (
                  <span className='account-btn__bonuses__decimal'>
                    ,{price?.floatPart}
                  </span>
                )}{' '}
                <span className='account-btn__bonuses__ruble'>₽</span>
              </div>
              <div className='account-btn__sub-title'>бонусов</div>
            </div>
          </div>
          <div
            onMouseEnter={() => {
              setIsShowMenu(true);
            }}
            onMouseLeave={() => {
              setIsShowMenu(false);
            }}
            className={`account-btn__menu-wrapper${
              isShowMenu ? '' : ' account-btn__menu-wrapper--hidden'
            }`}
          >
            <div className='account-btn__menu'>
              {listOfRoutes?.map((item, index) => {
                const isActive =
                  location.pathname.split('/r/')[0] === item.path ||
                  (location.pathname.includes('/cabinet/orders') &&
                    item.path === '/cabinet/orders');
                const onClick =
                  'isExit' in item && item.isExit
                    ? e => {
                        e.preventDefault();
                        logout();
                      }
                    : () => {
                        setIsShowMenu(false);
                      };
                const icon = isActive
                  ? listOfRoutes[index].iconActive
                  : curIcons[index].icon;
                return (
                  <Link
                    to={item.path || '/'}
                    key={item.title}
                    onClick={onClick}
                    data-id={index}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    className={`account-btn__item${
                      isActive ? ' account-btn__item--active' : ''
                    }`}
                  >
                    <Picture
                      icon={icon}
                      pictureClassName='account-btn__item__icon'
                      height='24'
                      width='24'
                    />
                    {item.title}
                  </Link>
                );
              })}

              <Link
                to={REVIEWS_LEAVE_FEEDBACK_LINK}
                className='account-btn__callback-btn'
              >
                {personalAreaFeedbackTitle}
              </Link>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div
          onClick={changeAuthModalState}
          onMouseEnter={() => {
            setLoginBtnIcon(personRedFilled);
          }}
          onMouseLeave={() => {
            setLoginBtnIcon(personBlackFilled);
          }}
          className='login-btn'
        >
          <Picture
            className='login-btn__icon'
            icon={loginBtnIcon}
            height='24'
            width='24'
          />
          Вход
        </div>
      );
    }
  }
});

export default AccountBtnDesktop;
